/**
 * Envío AP-4 · panel de «Editar imagen»: las 9 herramientas, deshacer y los
 * botones Cancelar / Aplicar. Lee y escribe la sesión de `imageEditStore`.
 */
import { useEffect } from 'react';
import { estimateOutput } from './imageEditActions';
import {
  X, Undo2, RotateCcw, RotateCw, FlipHorizontal2, FlipVertical2, Check, Loader2, Info, Trash2,
  WandSparkles, Crop, Blend, Proportions, Shapes, Frame, Palette, PenLine,
  Brush, MoveUpRight, Square, Circle, Type, Eraser, Scaling, AlertTriangle,
} from 'lucide-react';
import useImageEditStore from '../../store/imageEditStore';
import {
  FADE_SIDES, MARK_COLORS, MARK_TIPS, MARK_TOOLS, PRESETS, TOOLS, TOOL_TIPS,
  OUTPUT_FORMATS, OUTPUT_PRESETS, fmtBytes, needsTransparency, normalizeOutput, outputFromPreset, defaultPolygon, describeEdit, isIdentity,
} from '../../utils/imageEdit';

// AP-10 · íconos Lucide por herramienta (mismo set que el resto de la app).
export const TOOL_ICONS = {
  rapidos: WandSparkles, recortar: Crop, fundido: Blend, deformar: Proportions, forma: Shapes,
  borde: Frame, color: Palette, voltear: FlipHorizontal2, anotar: PenLine, tamano: Scaling,
};
export const MARK_ICONS = { path: Brush, arrow: MoveUpRight, rect: Square, ellipse: Circle, text: Type, erase: Eraser };

const BORDER_COLORS = [['#FFFFFF', 'Blanco'], ['#111111', 'Negro'], ['#FBBF24', 'Amarillo'], ['#22D3EE', 'Celeste']];
const SHAPES = [['rect', 'Rectángulo'], ['round', 'Redondeado'], ['ellipse', 'Elipse'], ['polygon', 'Forma libre']];

function Slider({ label, value, min, max, step = 1, unit = '%', onChange, testid }) {
  const checkpoint = useImageEditStore((s) => s.checkpoint);
  return (
    <label className="ie-field">
      <span>{label} · <b>{Math.round(value * 10) / 10}{unit}</b></span>
      <input type="range" min={min} max={max} step={step} value={value}
        onPointerDown={checkpoint} onKeyDown={checkpoint}
        onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} />
    </label>
  );
}

const Chip = ({ children, onClick, pressed, testid, title }) => (
  <button type="button" className={`ie-chip ${pressed ? 'is-on' : ''}`} onClick={onClick}
    aria-pressed={pressed} title={title} data-testid={testid}>{children}</button>
);

function centeredCrop(aspect, imgW, imgH) {
  if (!imgW || !imgH) return { t: 0, r: 0, b: 0, l: 0 };
  const a = imgW / imgH;
  if (a > aspect) {
    const keep = aspect / a; const side = ((1 - keep) / 2) * 100;
    return { t: 0, b: 0, l: +side.toFixed(1), r: +side.toFixed(1) };
  }
  const keep = a / aspect; const side = ((1 - keep) / 2) * 100;
  return { l: 0, r: 0, t: +side.toFixed(1), b: +side.toFixed(1) };
}

function ToolBody({ imgSize }) {
  const tool = useImageEditStore((s) => s.tool);
  const est = useImageEditStore((s) => s.estimate);
  const p = useImageEditStore((s) => s.params);
  const update = useImageEditStore((s) => s.update);
  const preset = useImageEditStore((s) => s.preset);
  const markTool = useImageEditStore((s) => s.markTool);
  const markColor = useImageEditStore((s) => s.markColor);
  const markWidth = useImageEditStore((s) => s.markWidth);
  const st = useImageEditStore.getState;
  const live = { commit: false };
  const swap = p.rotate === 90 || p.rotate === 270;
  const outW = swap ? imgSize.h : imgSize.w;
  const outH = swap ? imgSize.w : imgSize.h;

  switch (tool) {
    case 'rapidos':
      return (
        <>
          <p className="ie-hint">Un toque y listo. Después podés ajustar cada cosa.</p>
          <div className="ie-chips">
            {PRESETS.map(([id, label]) => <Chip key={id} onClick={() => preset(id)} testid={`ie-preset-${id}`}>{label}</Chip>)}
          </div>
        </>
      );
    case 'recortar':
      return (
        <>
          <p className="ie-hint">Arrastrá los bordes en el preview o dibujá un recuadro. La imagen queda en su lugar: sólo desaparece lo recortado.</p>
          <div className="ie-chips">
            <Chip onClick={() => update({ crop: { t: 0, r: 50, b: 0, l: 0 } })}>Mitad izquierda</Chip>
            <Chip onClick={() => update({ crop: { t: 0, r: 0, b: 0, l: 50 } })}>Mitad derecha</Chip>
            <Chip onClick={() => update({ crop: centeredCrop(16 / 9, outW, outH) })}>16:9</Chip>
            <Chip onClick={() => update({ crop: centeredCrop(1, outW, outH) })}>1:1</Chip>
            <Chip onClick={() => update({ crop: { t: 0, r: 0, b: 0, l: 0 } })} testid="ie-crop-reset">Sin recorte</Chip>
          </div>
          {[['l', 'Quitar desde la izquierda'], ['r', 'Quitar desde la derecha'], ['t', 'Quitar desde arriba'], ['b', 'Quitar desde abajo']].map(([k, label]) => (
            <Slider key={k} label={label} value={p.crop[k]} min={0} max={90} step={0.5}
              onChange={(v) => update({ crop: { ...p.crop, [k]: v } }, live)} testid={`ie-crop-${k}-range`} />
          ))}
        </>
      );
    case 'fundido':
      return (
        <>
          <p className="ie-hint">La imagen se vuelve transparente desde un lado. En el preview arrastrás las dos guías.</p>
          <div className="ie-seg" role="group" aria-label="Desde dónde">
            {FADE_SIDES.map(([k, label]) => (
              <button key={k} type="button" aria-pressed={p.fade.side === k} className={p.fade.side === k ? 'is-on' : ''}
                title={{ none: 'Sin fundido', left: 'Hacia la izquierda', right: 'Hacia la derecha', top: 'Hacia arriba', bottom: 'Hacia abajo', radial: 'Viñeta' }[k]}
                onClick={() => update({ fade: { ...p.fade, side: k } })} data-testid={`ie-fade-${k}`}>{label}</button>
            ))}
          </div>
          {p.fade.side !== 'none' && (
            <>
              <Slider label="Dónde empieza" value={p.fade.start} min={0} max={99}
                onChange={(v) => update({ fade: { ...p.fade, start: v } }, live)} testid="ie-fade-start-range" />
              <Slider label="Suavidad" value={p.fade.soft} min={1} max={100}
                onChange={(v) => update({ fade: { ...p.fade, soft: v } }, live)} />
            </>
          )}
        </>
      );
    case 'deformar':
      return (
        <>
          <p className="ie-hint">Mové las 4 esquinas libremente en el preview: la imagen toma esa forma, por ejemplo para entrar en una pantalla en perspectiva.</p>
          <p className="ie-hint">Se combina con Recortar, Fundido y Color: primero se ajusta el color, después se deforma y al final se recorta.</p>
          <div className="ie-chips">
            <Chip onClick={() => update({ warp: null })} testid="ie-warp-reset">Enderezar esquinas</Chip>
          </div>
        </>
      );
    case 'forma': {
      const current = p.shape.kind === 'rect' && p.shape.radius > 0 ? 'round' : p.shape.kind;
      return (
        <>
          <div className="ie-seg" role="group" aria-label="Forma">
            {SHAPES.map(([k, label]) => (
              <button key={k} type="button" aria-pressed={current === k} className={current === k ? 'is-on' : ''}
                onClick={() => {
                  if (k === 'round') update({ shape: { kind: 'rect', radius: p.shape.radius || 8, points: null } });
                  else if (k === 'rect') update({ shape: { kind: 'rect', radius: 0, points: null } });
                  else if (k === 'polygon') update({ shape: { kind: 'polygon', radius: 0, points: p.shape.points || defaultPolygon(p.crop) } });
                  else update({ shape: { kind: k, radius: 0, points: null } });
                }} data-testid={`ie-shape-${k}`}>{label}</button>
            ))}
          </div>
          {current === 'round' && (
            <Slider label="Redondeo de esquinas" value={p.shape.radius} min={0} max={50}
              onChange={(v) => update({ shape: { ...p.shape, radius: v } }, live)} />
          )}
          {current === 'polygon' && <p className="ie-hint">Arrastrá los puntos. Doble clic dentro agrega un punto en el borde más cercano; clic derecho sobre un punto lo quita.</p>}
          {current === 'ellipse' && <p className="ie-hint">La elipse ocupa el recuadro de «Recortar».</p>}
        </>
      );
    }
    case 'borde':
      return (
        <>
          <label className="ie-check">
            <input type="checkbox" checked={p.border.enabled} onChange={() => update({ border: { ...p.border, enabled: !p.border.enabled } })} data-testid="ie-border" />
            Borde
          </label>
          {p.border.enabled && (
            <>
              <div className="ie-swatches" role="group" aria-label="Color del borde">
                {BORDER_COLORS.map(([c, name]) => (
                  <button key={c} type="button" aria-label={name} aria-pressed={p.border.color === c}
                    className={p.border.color === c ? 'is-on' : ''} style={{ background: c }}
                    onClick={() => update({ border: { ...p.border, color: c } })} />
                ))}
              </div>
              <Slider label="Grosor" value={p.border.width} min={1} max={40} unit=""
                onChange={(v) => update({ border: { ...p.border, width: v } }, live)} />
            </>
          )}
          <label className="ie-check">
            <input type="checkbox" checked={p.shadow.enabled} onChange={() => update({ shadow: { ...p.shadow, enabled: !p.shadow.enabled } })} data-testid="ie-shadow" />
            Sombra suave
          </label>
          {p.shadow.enabled && (
            <Slider label="Intensidad" value={p.shadow.strength} min={10} max={100}
              onChange={(v) => update({ shadow: { ...p.shadow, strength: v } }, live)} />
          )}
        </>
      );
    case 'color':
      return (
        <>
          <Slider label="Brillo" value={p.color.brightness} min={40} max={160} onChange={(v) => update({ color: { ...p.color, brightness: v } }, live)} testid="ie-brightness" />
          <Slider label="Contraste" value={p.color.contrast} min={40} max={160} onChange={(v) => update({ color: { ...p.color, contrast: v } }, live)} />
          <Slider label="Saturación" value={p.color.saturation} min={0} max={200} onChange={(v) => update({ color: { ...p.color, saturation: v } }, live)} />
          <Slider label="Desenfoque" value={p.color.blur} min={0} max={12} step={0.5} unit="" onChange={(v) => update({ color: { ...p.color, blur: v } }, live)} />
          <label className="ie-check">
            <input type="checkbox" checked={p.color.grayscale} onChange={() => update({ color: { ...p.color, grayscale: !p.color.grayscale } })} />
            Blanco y negro
          </label>
        </>
      );
    case 'voltear':
      return (
        <div className="ie-chips">
          <Chip onClick={() => update({ flip: { ...p.flip, h: !p.flip.h } })} pressed={p.flip.h} testid="ie-flip-h"><FlipHorizontal2 size={14} aria-hidden="true" /> Espejo horizontal</Chip>
          <Chip onClick={() => update({ flip: { ...p.flip, v: !p.flip.v } })} pressed={p.flip.v}><FlipVertical2 size={14} aria-hidden="true" /> Espejo vertical</Chip>
          <Chip onClick={() => update({ rotate: p.rotate - 90 })} testid="ie-rot-l"><RotateCcw size={14} aria-hidden="true" /> Girar 90°</Chip>
          <Chip onClick={() => update({ rotate: p.rotate + 90 })} testid="ie-rot-r"><RotateCw size={14} aria-hidden="true" /> Girar 90°</Chip>
        </div>
      );
    case 'anotar':
      return (
        <>
          <p className="ie-hint">Pincel, flechas, formas y texto encima de la imagen, por ejemplo para marcarle al modelo qué cambiar.</p>
          <div className="ie-marks" role="group" aria-label="Herramienta de anotar">
            {MARK_TOOLS.map(([k, label]) => {
              const Icon = MARK_ICONS[k];
              return (
                <button key={k} type="button" aria-pressed={markTool === k} className={markTool === k ? 'is-on' : ''}
                  title={`${label} · ${MARK_TIPS[k]}`} aria-label={label}
                  onClick={() => st().setMarkTool(k)} data-testid={`ie-mark-${k}`}>
                  {Icon && <Icon size={18} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
          {(() => {
            const cur = MARK_TOOLS.find(([k]) => k === markTool);
            return cur ? <p className="ie-mark-tip" data-testid="ie-mark-tip"><b>{cur[1]}</b> · {MARK_TIPS[cur[0]]}</p> : null;
          })()}
          <div className="ie-swatches" role="group" aria-label="Color">
            {MARK_COLORS.map(([c, name]) => (
              <button key={c} type="button" aria-label={name} aria-pressed={markColor === c}
                className={markColor === c ? 'is-on' : ''} style={{ background: c }} onClick={() => st().setMarkColor(c)} />
            ))}
          </div>
          <label className="ie-field">
            <span>Grosor · <b>{markWidth}</b></span>
            <input type="range" min={2} max={20} value={markWidth} onChange={(e) => st().setMarkWidth(Number(e.target.value))} />
          </label>
          <div className="ie-chips">
            <Chip onClick={() => update({ marks: p.marks.slice(0, -1) })} testid="ie-mark-undo">Quitar la última</Chip>
            <Chip onClick={() => update({ marks: [] })}><Trash2 size={13} aria-hidden="true" /> Borrar todo</Chip>
          </div>
          <p className="ie-hint">{p.marks.length ? `${p.marks.length} marca${p.marks.length > 1 ? 's' : ''}` : 'Sin marcas todavía'}</p>
        </>
      );
    case 'tamano': {
      const o = p.output;
      const setOut = (patch) => update({ output: normalizeOutput({ ...o, ...patch, preset: 'custom' }) });
      const side = o.maxW && o.maxH && o.maxW === o.maxH ? o.maxW : (o.maxW || o.maxH || '');
      const lossy = o.format !== 'png';
      return (
        <>
          <p className="ie-hint">Elegí el destino o ajustalo a mano. Se guarda como imagen nueva con ese tamaño, formato y peso.</p>
          <div className="ie-size-presets" role="group" aria-label="Destino">
            {OUTPUT_PRESETS.map((pr) => (
              <button key={pr.key} type="button" aria-pressed={o.preset === pr.key}
                className={`ie-size-preset ${o.preset === pr.key ? 'is-on' : ''}`}
                onClick={() => update({ output: outputFromPreset(pr.key) })} data-testid={`ie-size-${pr.key}`}>
                <b>{pr.label}</b><small>{pr.hint}</small>
              </button>
            ))}
          </div>
          <div className="ie-size-grid">
            <label className="ie-field">
              <span>Lado mayor (px)</span>
              <input type="number" min={64} max={4096} step={10} value={side} placeholder="Original"
                onChange={(e) => { const v = e.target.value; setOut({ maxW: v || null, maxH: v || null, anyOrientation: true }); }}
                data-testid="ie-size-side" />
            </label>
            <label className="ie-field">
              <span>Peso máximo (MB)</span>
              <input type="number" min={0.05} max={50} step={0.1} value={o.maxMB ?? ''} placeholder="Sin tope"
                onChange={(e) => setOut({ maxMB: e.target.value || null })} data-testid="ie-size-maxmb" />
            </label>
          </div>
          <div className="ie-field">
            <span>Formato</span>
            <div className="ie-seg" role="group" aria-label="Formato">
              {OUTPUT_FORMATS.map(([k, label]) => (
                <button key={k} type="button" aria-pressed={o.format === k} className={o.format === k ? 'is-on' : ''}
                  onClick={() => setOut({ format: k })} data-testid={`ie-size-fmt-${k}`}>{label}</button>
              ))}
            </div>
          </div>
          {lossy && (
            <Slider label="Calidad" value={Math.round(o.quality * 100)} min={40} max={100} unit=""
              onChange={(v) => update({ output: normalizeOutput({ ...o, quality: v / 100, preset: 'custom' }) }, live)} testid="ie-size-quality" />
          )}
          {o.format === 'jpeg' && needsTransparency(p) && (
            <p className="ie-warn" role="note"><AlertTriangle size={13} aria-hidden="true" /> JPG no tiene transparencia: las zonas transparentes quedan en blanco. Usá PNG o WebP si las necesitás.</p>
          )}
          <div className="ie-size-result" data-testid="ie-size-result" aria-live="polite">
            <span className="ie-size-from">{outW}×{outH}</span>
            <span aria-hidden="true">→</span>
            {est ? (
              <span className="ie-size-to"><b>{est.width}×{est.height}</b> · {est.format === 'jpeg' ? 'JPG' : est.format.toUpperCase()} · <b>{fmtBytes(est.bytes)}</b>
                {!est.fits && <em> · no llega al peso pedido</em>}</span>
            ) : <span className="ie-size-to">calculando…</span>}
          </div>
        </>
      );
    }
    default:
      return null;
  }
}

export default function ImageEditPanel({
  title = 'Editar imagen', sourceLabel, imgSize = { w: 0, h: 0 }, onCancel, onApply, applying = false,
  note, footerExtra = null, applyLabel = 'Aplicar', className = '',
}) {
  const tool = useImageEditStore((s) => s.tool);
  const params = useImageEditStore((s) => s.params);
  const history = useImageEditStore((s) => s.history);
  const st = useImageEditStore.getState;
  const summary = describeEdit(params);
  const nothing = isIdentity(params);

  // AP-14 · estimación en vivo de la pestaña «Tamaño» (resolución y peso reales)
  const img = useImageEditStore((s) => s.img);
  useEffect(() => {
    if (tool !== 'tamano' || !img) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      estimateOutput({ img, params })
        .then((e) => { if (alive) st().setEstimate(e); })
        .catch(() => { if (alive) st().setEstimate(null); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [tool, img, params, st]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.closest?.('input[type="text"], textarea, [contenteditable="true"]')) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.stopPropagation(); st().undo(); }
      if (e.key === 'Escape') { e.stopPropagation(); onCancel?.(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel, st]);

  return (
    <aside className={`ie-panel ${className}`} aria-label="Editar imagen" data-testid="ie-panel">
      <div className="ie-head">
        <span className="ie-head-txt">
          <span className="ie-kicker">{title.toUpperCase()}</span>
          {sourceLabel && <span className="ie-source" title={sourceLabel}>{sourceLabel}</span>}
        </span>
        <button type="button" className="ie-icon-btn" onClick={() => st().undo()} disabled={!history.length}
          title="Deshacer (Ctrl+Z)" aria-label="Deshacer" data-testid="ie-undo"><Undo2 size={15} /></button>
        <button type="button" className="ie-icon-btn" onClick={onCancel} title="Cerrar sin aplicar" aria-label="Cerrar sin aplicar" data-testid="ie-close"><X size={16} /></button>
      </div>

      <div className="ie-tabs" role="tablist" aria-label="Herramientas">
        {TOOLS.map(([k, label]) => {
          const Icon = TOOL_ICONS[k];
          return (
            <button key={k} type="button" role="tab" aria-selected={tool === k} className={tool === k ? 'is-on' : ''}
              title={`${label} · ${TOOL_TIPS[k]}`}
              onClick={() => st().setTool(k)} data-testid={`ie-tool-${k}`}>
              {Icon && <Icon size={20} aria-hidden="true" />}
              <span>{label}</span>
            </button>
          );
        })}
      </div>
      {(() => {
        const cur = TOOLS.find(([k]) => k === tool);
        const Icon = cur && TOOL_ICONS[cur[0]];
        return cur ? (
          <div className="ie-tool-tip" data-testid="ie-tool-tip">
            {Icon && <Icon size={18} aria-hidden="true" />}
            <span><b>{cur[1]}</b> · {TOOL_TIPS[cur[0]]}</span>
          </div>
        ) : null;
      })()}

      <div className="ie-body" role="tabpanel">
        <ToolBody imgSize={imgSize} />
      </div>

      <div className="ie-foot">
        {summary && <p className="ie-summary" data-testid="ie-summary">{summary}</p>}
        <div className="ie-note" role="note">
          <Info size={15} aria-hidden="true" />
          <span>{note || <>Al aplicar se crea una <b>imagen nueva</b> en tu Galería. <b>El original no cambia</b>.</>}</span>
        </div>
        {footerExtra}
        <div className="ie-actions">
          <button type="button" className="ie-btn ghost" onClick={() => st().reset()} disabled={nothing || applying} data-testid="ie-reset">Empezar de nuevo</button>
          <button type="button" className="ie-btn ghost" onClick={onCancel} disabled={applying}>Cancelar</button>
          <button type="button" className="ie-btn primary" onClick={onApply} disabled={nothing || applying} data-testid="ie-apply">
            {applying ? <Loader2 size={15} className="ie-spin" aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}
            {applying ? 'Guardando…' : applyLabel}
          </button>
        </div>
      </div>
    </aside>
  );
}
